import Location from "../../hooks/Location"
import { useFirestoreID, useFirestoreGeneralOrderBy, useFirestoreGeneral } from "../../firebase/useFirestore";
import { useState, useEffect, useContext, useMemo } from "react";
import useSettings from "../../hooks/Settings";
import { Auth } from "../../StateManagment/Auth";
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import Tooltip from "../../components/common/Tooltip";
import { useHistory } from "react-router-dom";
import { client } from "../../hooks/Client";
import CategorizeFieldNotice from "../../components/researchResults/CategorizeFieldNotice";
import FieldResponsesCount from "../../components/Research/FieldResponsesCount";
import { db } from "../../firebase/config";
import firebase from '../../firebase/compat';
import MomentMeta from "../../components/Research/MomentMeta";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import SessionResponsesCount from "../../components/Research/SessionResponsesCount";
import FormResponsesCount from "../../components/Research/FormResponsesCount";
import EffectMeta from "../../components/effects/EffectMeta";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import SelectableGrid from "../../components/common/SelectableGrid";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import filterSessionsWithResponses from "../../helpers/filterSessionsWithResponses";
import groupResponsesIntoForms from "../../helpers/groupResponsesIntoForms";
import DeleteModal from "../../components/common/DeleteModal";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import softDeleteDoc from "../../helpers/softDeleteDoc";
import softDeleteDocs from "../../helpers/softDeleteDocs";
import notDeleted from "../../helpers/notDeleted";

const ResearchResults = () => {
  // Context
  const [auth] = useContext(Auth)
  const [, , , setSavedMessage] = useContext(SavedIcon)

 //State
 const [questionnaireID, setQuestionnaireID] = useState('')
 const [researchTitle, setResearchTitle] = useState('')
 const [title, setTitle] = useState('')
const [resultType, setResultType] = useState('forms');
const [momentCounts, setMomentCounts] = useState([]);
const [deleteModalOpen, setDeleteModalOpen] = useState(false);
const [sessionModalOpen, setSessionModalOpen] = useState(false);
const [sessionToDelete, setSessionToDelete] = useState("");
const [formToDelete, setFormToDelete] = useState(/** @type {import('../../helpers/groupResponsesIntoForms').FormGroup | null} */ (null));
const [deleteError, setDeleteError] = useState('');

 // Hooks
 const researchId = Location()[3]
 const history = useHistory()
 const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };

 // Firestore
 const research = useFirestoreID('Research', researchId ? researchId : '')
 const fields = useFirestoreGeneralOrderBy('QuestionnaireFields', 'QuestionnaireID', questionnaireID ? questionnaireID : '', 'Position', 'asc')
   const results = useFirestoreGeneral('QuestionnairesResponses', 'ResearchID', researchId ? researchId : '');
   const momentsRaw = useFirestoreGeneralOrderBy('MeasureMoments', 'ResearchID', researchId ? researchId : '', 'Position', 'asc');
   const moments = useMemo(() => (momentsRaw || []).filter((m) => !m.Deleted), [momentsRaw]);
   const sessions = useFirestoreGeneralOrderBy('Sessions', 'ResearchID', researchId ? researchId : '', 'Timestamp', 'desc');

   // Only count sessions that actually have answers. A session is created on page
   // load (before answering), so previews/reloads/abandoners create empty sessions.
   const sessionsWithResponses = useMemo(
       () => filterSessionsWithResponses(sessions, results),
       [sessions, results]
   );

 useEffect(() => {
     research && research.forEach(item => {
         setQuestionnaireID(item.QuestionnaireID)
         setResearchTitle(item.Title)
     })
 },[research])

// Set field title
const fieldTitleType = (item) => {
    switch(item.Type){
        case 'paragraph':
            return item.Question
        case 'scale':
            return item.Question
        case 'number':
            return item.Question
        case 'multiple-one':
            return item.Question
        case 'multiple-multiple':
            return item.Question
        case 'matrix-one':
            return matrixType(item)
        case 'matrix-multiple':
            return matrixType(item)
    }
}

// Matrix type
const matrixType = (item) => {

    if(item.Standard === 'true'){

        return (
            <div id='meetstandaard-effect-meta-container'>
                <Tooltip content={'Gestandaardiseerd effect'} width='80%' right='-120px' top='-15px'>
                <p className='table-p tooltip-p-normal' id='add-inicator-meetstandaard-effect-title-container'>
                    <EffectMeta item={item.EffectId[0]}/>
                </p>
                </Tooltip>
                <a href="https://meetstandaard.nl" target="_blank" rel="noopener noreferrer" className="question-standardized-sufix">(meetstandaard.nl)</a>
            </div>
        )

    } else {
        return item.Question
    }
}

// Group responses into submitted forms (by FormID; timestamp fallback for
    // legacy responses without one) — grouping by timestamp alone merges
    // respondents that were saved in the same millisecond.
    const forms = useMemo(() => groupResponsesIntoForms(results), [results]);


    // Group forms by moment
    const getFormGroupsByMoment = () => {
        // `forms` is your state from the timestamp grouping
        if (!forms) return [];
      
        // Group the forms by momentID
        const byMoment = forms.reduce((acc, formGroup) => {
          const m = formGroup.momentID;
          if (!acc[m]) {
            acc[m] = {
              momentID: m,
              forms: [],
            };
          }
          acc[m].forms.push(formGroup);
          return acc;
        }, {});
      
        // Turn into an array, compute counts, and sort by count desc
        return Object.values(byMoment)
          .map(({ momentID, forms }) => ({
            momentID,
            count: forms.length,
            forms,
          }))
          .sort((a, b) => b.count - a.count);
      };

      const groups = getFormGroupsByMoment();

      // Count prefix
        const countPrefix = (count) => {
            if (count === 1) {
                return 'formulier';
            } else {
                return 'formulieren';
            }
        }

// Query all response docs belonging to one form group. FormID identifies the
// form; legacy groups without a FormID fall back to their shared Timestamp.
// Direct read past the hooks, so the tenant filter is on the query itself.
// AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
/** @param {import('../../helpers/groupResponsesIntoForms').FormGroup} formGroup */
const formResponsesQuery = (formGroup) => {
    const base = db
      .collection('QuestionnairesResponses')
      .where('CompagnyID', '==', client)
      .where('ResearchID', '==', researchId);

    return formGroup.formID
      ? base.where('FormID', '==', formGroup.formID)
      : base.where('Timestamp', '==', formGroup.items[0].Timestamp);
};

// Update moment handler
/** @param {import('../../helpers/groupResponsesIntoForms').FormGroup} formGroup */
const updateMomentHandler = async (formGroup, newMomentID) => {
    const snapshot = await formResponsesQuery(formGroup).get();

    if (snapshot.empty) {
      console.error("No matching docs");
      return;
    }

    snapshot.docs.forEach(doc =>
      doc.ref.update({ MomentID: newMomentID })
    );

  };

// Delete form
// Zacht (ADR-008): alle nog zichtbare antwoorden van het formulier gaan samen de
// prullenbak in. Gooit bij een mislukking door, zodat het scherm het kan zeggen.
// AI-CONTEXT: See docs/pitfalls.md#error-catch-all
/** @param {import('../../helpers/groupResponsesIntoForms').FormGroup} formGroup */
const deleteForm = async (formGroup) => {
  const snapshot = await formResponsesQuery(formGroup).get();
  const docids = snapshot.docs
    .filter((doc) => notDeleted(doc.data()))
    .map((doc) => doc.id);

  if (docids.length === 0) {
    throw new Error('Geen antwoorden gevonden bij dit formulier.');
  }

  await softDeleteDocs({
    collection: 'QuestionnairesResponses',
    docids,
    authId: auth?.ID,
    title: `Ingevuld formulier — ${researchTitle}, ${formGroup.date.toLocaleDateString('nl-NL', options)}`,
  });
}

// Confirm deleting the selected form (called from the delete modal)
const confirmDeleteForm = async () => {
  setDeleteError('');
  const formGroup = formToDelete;
  setDeleteModalOpen(false);
  setFormToDelete(null);
  if (!formGroup) return;

  try {
    await deleteForm(formGroup);
    setSavedMessage('Formulier naar de prullenbak');
  } catch (error) {
    console.error('Fout bij verwijderen formulier:', error);
    setDeleteError(`Het formulier is niet verwijderd: ${error.message}`);
  }
}

// Delete session
// Eerst bevestigen: één klik verwijderde de sessie meteen.
const askDeleteSession = (docid) => {
  if (!docid) return;
  setSessionToDelete(docid);
  setSessionModalOpen(true);
}

// Zacht (ADR-008), en een mislukking komt op het scherm.
const deleteSession = async () => {
  if (!sessionToDelete) return;
  setDeleteError('');

  try {
    await softDeleteDoc({
      collection: 'Sessions',
      docid: sessionToDelete,
      authId: auth?.ID,
      title: `Sessie — ${researchTitle}`,
    });
    setSavedMessage('Sessie naar de prullenbak');
  } catch (error) {
    console.error('Fout bij verwijderen sessie:', error);
    setDeleteError(`De sessie is niet verwijderd: ${error.message}`);
  }
}

return (
        <div className="main">
            <div className="main-container">
                <div className='page-header'>
                    <h1>{researchTitle}</h1>
                </div>
                {deleteError && <p className="research-results-delete-error" role="alert">{deleteError}</p>}
                {/* <div id="research-results-import-button-container" onClick={() => {history.push(`/${client}/importresearchresults/${researchId}`)}}>
                    <UploadOutlinedIcon/>
                    <p>Responses importeren</p>
                </div> */}
                {/* Verwijderen (naar de prullenbak) mag iedereen met bewerkrechten op
                    metingen, bijv. voor testinvullingen; verzoek Nikki Kolman 2026-10-06. */}
                <ReadOnlyGuard edit="measurement.edit">
                  {(canEdit) => (
                <div className="add-results-container">
                    <SelectableGrid
                        items={[
                            { id: 'forms', label: 'Formulieren' },
                            { id: 'questions', label: 'Lijst met vragen' },
                            { id: 'sessions', label: 'Sessies' },
                        ]}
                        getId={(item) => item.id}
                        getLabel={(item) => item.label}
                        selectedId={resultType}
                        onClick={setResultType}
                    />
                    {resultType === 'questions' &&
                    <Table
                        columns={[
                            { key: 'question', label: 'VRAAG' },
                            { key: 'responses', label: 'ANTWOORDEN', searchable: false },
                            { key: 'categorization', label: 'CATEGORISATIE', searchable: false },
                        ]}
                        items={fields || []}
                        getItemKey={(item) => item.ID}
                        renderRow={(item) => (
                            <>
                                <TableCell>
                                    <p>{fieldTitleType(item)}</p>
                                </TableCell>
                                <TableCell>
                                    <div id='indicator-effect-icons-container' onClick={() => {history.push(`/${client}/researchresponses/${item.ID}/${researchId}`)}}>
                                        <Tooltip content='Bekijk de responses' top={'-60px'} width={'30px'}>
                                            <ChatBubbleOutlineOutlinedIcon className='table-delete-icon'/>
                                        </Tooltip>
                                        <FieldResponsesCount fieldID={item.ID} researchID={researchId}/>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <CategorizeFieldNotice fieldID={item.ID} researchID={researchId} type={item.Type}/>
                                </TableCell>
                            </>
                        )}
                        searchAccessor={(item) => item?.Question || ''}
                        searchPlaceholder="Zoeken op vraag"
                        emptyMessage="Geen vragen gevonden."
                    />
                }

                {/* Forms */}
                 {resultType === 'forms' &&
                 <div>
                    <Table
                        columns={[
                            { key: 'date', label: 'DATUM' },
                            {
                                key: 'moment',
                                label: 'MEETMOMENT',
                                searchable: false,
                                headerContent: (
                                    <div id='reserach-results-forms-count-outer-container'>
                                        <h3>MEETMOMENT </h3>
                                        <div id='reserach-results-forms-count-container'>
                                            {groups.map(({ momentID, count }) => (
                                                <div key={momentID} id='reserach-results-forms-count-container'>
                                                    <MomentMeta item={momentID} /><p>: {count} {countPrefix(count)}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ),
                            },
                            { key: 'responses', label: 'ANTWOORDEN', searchable: false },
                            ...(canEdit ? [{ key: 'delete', label: 'VERWIJDEREN', searchable: false }] : []),
                        ]}
                        items={forms || []}
                        getItemKey={(item) => item.key}
                        renderRow={(item) => (
                            <>
                                <TableCell>
                                    <p>{item.date.toLocaleDateString('nl-NL', options)}</p>
                                </TableCell>
                                <TableCell>
                                    <div id='reserach-results-moment-edit-container'>
                                        <select name="" id="" value={item.momentID} onChange={e => updateMomentHandler(item, e.target.value)}>
                                            {moments && moments.map(moment => (
                                                <option
                                                    key={moment.ID}
                                                    value={moment.ID}
                                                >
                                                    {moment.Title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div 
                                        id='indicator-effect-icons-container' 
                                        onClick={() => {
                                            // FormID identifies the form; legacy groups without one
                                            // fall back to the old "seconds-nanoseconds" URL shape.
                                            if (item.formID) {
                                                history.push(`/${client}/researchresultsforms/${researchId}/${item.formID}`);
                                                return;
                                            }
                                            const ts = item.date;
                                            let firebaseTimestamp;
                                            if (ts.toDate) {
                                                firebaseTimestamp = ts;
                                            } else {
                                                firebaseTimestamp = new firebase.firestore.Timestamp(
                                                    Math.floor(ts.getTime() / 1000),
                                                    (ts.getTime() % 1000) * 1000000
                                                );
                                            }
                                            const serializedTimestamp = `${firebaseTimestamp.seconds}-${firebaseTimestamp.nanoseconds}`;
                                            history.push(`/${client}/researchresultsforms/${researchId}/${serializedTimestamp}`);
                                        }}
                                    >
                                        <Tooltip content='Bekijk de responses' top={'-60px'} width={'30px'}>
                                            <ChatBubbleOutlineOutlinedIcon className='table-delete-icon' />
                                        </Tooltip>
                                        <p>
                                            <FormResponsesCount items={item.items}/>
                                        </p>
                                    </div>
                                </TableCell>
                                { canEdit &&
                                <TableCell>
                                    <Tooltip content='Verwijder formulier' top={'-60px'}>
                                        <DeleteOutlinedIcon
                                            className='table-delete-icon'
                                            onClick={() => {
                                                setFormToDelete(item);
                                                setDeleteModalOpen(true);
                                            }}
                                        />
                                    </Tooltip>
                                </TableCell>
                                }
                            </>
                        )}
                        searchAccessor={(item) => item?.date?.toLocaleDateString('nl-NL', options) || ''}
                        searchPlaceholder="Zoeken op datum"
                        emptyMessage="Geen formulieren gevonden."
                    />
                </div>
                }

                {/* Sessions */}
                 {resultType === 'sessions' &&
                 <div>
                    <Table
                        columns={[
                            { key: 'date', label: 'DATUM' },
                            { key: 'moment', label: 'MEETMOMENT', searchable: false },
                            { key: 'responses', label: 'ANTWOORDEN', searchable: false },
                            ...(canEdit ? [{ key: 'delete', label: 'VERWIJDEREN', searchable: false }] : []),
                        ]}
                        items={sessionsWithResponses}
                        getItemKey={(item) => item.ID}
                        renderRow={(item) => (
                            <>
                                <TableCell>
                                    <p>{item.Timestamp.toDate().toLocaleDateString('nl-NL', options)}</p>
                                </TableCell>
                                <TableCell>
                                    <MomentMeta item={item.MomentID} />
                                </TableCell>
                                <TableCell>
                                    <div 
                                        id='indicator-effect-icons-container' 
                                        onClick={() => {
                                            history.push(`/${client}/researchresultssessions/${researchId}/${item.SessionId}`);
                                        }}
                                    >
                                        <Tooltip content='Bekijk de responses' top={'-60px'} width={'30px'}>
                                            <ChatBubbleOutlineOutlinedIcon className='table-delete-icon' />
                                        </Tooltip>
                                        <p>
                                            <SessionResponsesCount sessionID={item.SessionId} researchID={researchId}/>
                                        </p>
                                    </div>
                                </TableCell>
                                { canEdit &&
                                <TableCell>
                                    <Tooltip content='Verwijder formulier' top={'-60px'}>
                                        <DeleteOutlinedIcon
                                            className='table-delete-icon'
                                            onClick={() => askDeleteSession(item.docid)}
                                        />
                                    </Tooltip>
                                </TableCell>
                                }
                            </>
                        )}
                        searchAccessor={(item) => item?.Timestamp?.toDate?.().toLocaleDateString('nl-NL', options) || ''}
                        searchPlaceholder="Zoeken op datum"
                        emptyMessage="Geen sessies gevonden."
                    />
                </div>
                }
                </div>
                  )}
                </ReadOnlyGuard>
            </div>

            <DeleteModal
                deleteModal={deleteModalOpen}
                setDeleteModal={setDeleteModalOpen}
                deleteItem={confirmDeleteForm}
                deleteName={`het formulier van ${formToDelete ? formToDelete.date.toLocaleDateString('nl-NL', options) : ''}`}
                softDelete
            />

            <DeleteModal
                deleteModal={sessionModalOpen}
                setDeleteModal={setSessionModalOpen}
                deleteItem={deleteSession}
                deleteName={'deze sessie'}
                softDelete
            />
        </div>
    )
}

export default ResearchResults
